import { Link } from "react-router-dom";
import { ArrowRight, Users, Briefcase, Globe, Heart, Award, Lightbulb } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SEO } from "../components/SEO";

export const Recruitment = () => {
  return (
    <div className="min-h-screen bg-navy-900 pt-20">
      <SEO 
        title="Recruitment | Magic C Plumbing | Plumber & Heating Engineer in London"
        description="Join Magic C Plumbing's international team. We're looking for talented designers, IT experts, and engineers passionate about luxury design and innovation."
        canonical="/recruitment"
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-sage-500/20 text-sage-300 px-6 py-2 rounded-full text-sm font-medium mb-6 backdrop-blur-md border border-sage-500/30">
              <Users className="inline h-4 w-4 mr-2" />
              Join Our International Team
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Join Our Team
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-10 leading-relaxed">
              We are always looking for passionate talents to join our growing international team.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
              At Magic C Plumbing, we offer exceptional opportunities to work on high-end, innovative projects that combine luxury design, advanced technology, and environmental responsibility.
            </p>
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                We Are Currently Looking For
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <Card className="bg-white/5 backdrop-blur-md border-sage-400/30 hover:bg-white/10 hover:border-sage-400/50 transition-all">
                <CardContent className="p-8 text-center">
                  <Lightbulb className="h-16 w-16 text-sage-400 mx-auto mb-6" />
                  <h3 className="text-2xl font-semibold text-white mb-4">Talented Designers</h3>
                  <p className="text-gray-300">Interior & Bathroom Design</p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-sage-400/30 hover:bg-white/10 hover:border-sage-400/50 transition-all">
                <CardContent className="p-8 text-center">
                  <Briefcase className="h-16 w-16 text-sage-400 mx-auto mb-6" />
                  <h3 className="text-2xl font-semibold text-white mb-4">IT Experts</h3>
                  <p className="text-gray-300">Developers & Tech Specialists</p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-sage-400/30 hover:bg-white/10 hover:border-sage-400/50 transition-all">
                <CardContent className="p-8 text-center">
                  <Award className="h-16 w-16 text-sage-400 mx-auto mb-6" />
                  <h3 className="text-2xl font-semibold text-white mb-4">Engineers</h3>
                  <p className="text-gray-300">Technical, Mechanical & Plumbing</p>
                </CardContent>
              </Card>
            </div>

            <div className="bg-sage-600/10 backdrop-blur-md border border-sage-400/30 rounded-2xl p-10 text-center">
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                If you are passionate about luxury design, innovation, and creating exceptional spaces, we want to hear from you.
              </p>
              <p className="text-lg text-white font-semibold mb-2">
                Send us your CV and a short introduction to:
              </p>
              <a href="mailto:recruitment@magiccplumbing.com" className="text-2xl text-sage-400 hover:text-sage-300 font-semibold transition-colors">
                recruitment@magiccplumbing.com
              </a>
              <p className="text-sm text-gray-400 mt-6">
                Selected candidates will be contacted for a personal interview.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-20 bg-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Opportunities at Magic C Plumbing
              </h2>
              <p className="text-xl text-gray-300">
                Join a dynamic, innovative company where every project is unique and where your creativity and expertise can truly shine.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
                <CardContent className="p-8">
                  <Globe className="h-12 w-12 text-sage-400 mb-4" />
                  <h3 className="text-2xl font-semibold text-white mb-3">
                    Prestigious International Projects
                  </h3>
                  <p className="text-gray-300">
                    Work on high-end projects across Wimbledon, Paris, Bali, and beyond, creating luxury spaces that inspire.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
                <CardContent className="p-8">
                  <Users className="h-12 w-12 text-sage-400 mb-4" />
                  <h3 className="text-2xl font-semibold text-white mb-3">
                    Passionate & Multicultural Team
                  </h3>
                  <p className="text-gray-300">
                    Be part of an international team of designers, engineers, and craftsmen united by excellence and innovation.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
                <CardContent className="p-8">
                  <Lightbulb className="h-12 w-12 text-sage-400 mb-4" />
                  <h3 className="text-2xl font-semibold text-white mb-3">
                    Cutting-Edge Innovation
                  </h3>
                  <p className="text-gray-300">
                    Participate in projects combining art, technology, and sustainability with the latest design and engineering solutions.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
                <CardContent className="p-8">
                  <Heart className="h-12 w-12 text-sage-400 mb-4" />
                  <h3 className="text-2xl font-semibold text-white mb-3">
                    Growth & Recognition
                  </h3>
                  <p className="text-gray-300">
                    Grow in a company that values creativity, precision, and responsibility. Your talent deserves recognition.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Create Beauty?
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              Join Magic C Plumbing and become part of a team that transforms visions into timeless works of art.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:recruitment@magiccplumbing.com">
                <Button size="lg" className="bg-sage-600 hover:bg-sage-500 text-white text-xl px-12 py-8 font-semibold">
                  Send Your Application
                  <ArrowRight className="ml-3 h-6 w-6" />
                </Button>
              </a>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-2 border-white/80 text-white hover:bg-white hover:text-navy-900 text-xl px-12 py-8">
                  Learn About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
